import { createFileRoute, Link } from "@tanstack/react-router";
import { getBooks, type PublicBook } from "@/lib/books.functions";
import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import { preloadImageLink } from "@/lib/images";
import { PlaceholderArt } from "@/components/site/PlaceholderArt";
import { SITE_URL, absoluteUrl } from "@/lib/site";
import {
  BOOKS_LIBRARY_ID,
  breadcrumbs,
  itemList,
  ldScript,
  orgRef,
  pageNode,
  personRef,
  seoMeta,
} from "@/lib/schema";

const TITLE = "Books by Buddha KA | 47 Gates of Consciousness & KAfrequency Systems";
const DESCRIPTION =
  "Books by Buddha KA, including 47 Gates of Consciousness and The Crystallized Mind — written systems from the KAfrequency canon, built as practice rather than commentary.";

export const Route = createFileRoute("/books/")({
  loader: async () => ({ books: await getBooks() }),
  component: BooksIndex,
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <p className="text-gold">Books are temporarily unavailable.</p>
      <Link to="/" className="mt-4 inline-block text-gold-luminous">← Return home</Link>
    </div>
  ),
  head: ({ loaderData }) => {
    const books = loaderData?.books ?? [];
    // Cover image only when a real cover exists in the catalog.
    const cover = books.find((b) => b.featured && b.coverUrl)?.coverUrl ?? books.find((b) => b.coverUrl)?.coverUrl;
    return {
      ...seoMeta({
        title: TITLE,
        description: DESCRIPTION,
        path: "/books",
        ...(cover ? { image: absoluteUrl(cover), imageAlt: "Book cover artwork by Buddha KA" } : {}),
      }),
      // This array replaces seoMeta()'s links, so the production canonical is
      // re-declared here alongside the LCP preload for the featured cover.
      links: [
        { rel: "canonical", href: absoluteUrl("/books") },
        cover ? preloadImageLink(cover, "(max-width: 768px) 92vw, 384px") : undefined,
      ].filter(Boolean) as Array<Record<string, string>>,
      scripts: [
        ldScript(
          pageNode({
            type: "CollectionPage",
            id: BOOKS_LIBRARY_ID,
            url: `${SITE_URL}/books`,
            name: "Books by Buddha KA",
            description: DESCRIPTION,
            about: [personRef(), orgRef()],
            extra: {
              author: personRef(),
              publisher: orgRef(),
              ...(books.length
                ? {
                    mainEntity: itemList(
                      books.map((b) => ({ path: `/books/${b.slug}`, name: b.title })),
                    ),
                  }
                : {}),
            },
          }),
        ),
        ldScript(breadcrumbs([["Books", "/books"]])),
      ],
    };
  },
});


function BooksIndex() {
  const { books } = Route.useLoaderData() as { books: PublicBook[] };
  const featured = books.find((b) => b.featured);
  const rest = books.filter((b) => b.slug !== featured?.slug);

  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Books & Systems</p>
      <h1 className="mt-4 font-serif text-5xl sm:text-6xl md:text-7xl">Instruction, in the hand.</h1>

      {featured && (
        <div className="mt-16 grid gap-10 rounded-2xl border hairline bg-charcoal/60 p-6 sm:p-10 md:grid-cols-[minmax(0,1fr)_1.2fr]">
          <Link
            to="/books/$slug"
            params={{ slug: featured.slug }}
            className="group block max-w-sm justify-self-center md:justify-self-start"
            aria-label={`${featured.title} — open book page`}
          >
            {featured.coverUrl ? (
              <div className="overflow-hidden rounded-lg border hairline bg-temple shadow-gold">
                <ResponsiveImage
                  src={featured.coverUrl}
                  alt={featured.coverAlt ?? `${featured.title} — cover art`}
                  priority
                  sizes="(max-width: 768px) 92vw, 384px"
                  className="h-auto w-full transition duration-500 group-hover:scale-[1.02]"
                />
              </div>
            ) : (
              <PlaceholderArt label={`[${featured.title} cover]`} aspect="portrait" />
            )}
          </Link>
          <div className="flex flex-col justify-center">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Featured Book</p>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl">{featured.title}</h2>
            {featured.author && (
              <p className="mt-2 text-sm text-muted-foreground">
                by <span className="text-ivory">{featured.author}{featured.authorLegalName ? ` (${featured.authorLegalName})` : ""}</span>
              </p>
            )}
            <p className="mt-4 max-w-xl font-serif text-lg italic text-ivory/90">{featured.subtitle}</p>
            <p className="mt-4 max-w-xl text-sm text-muted-foreground">{featured.longDescription}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/books/$slug"
                params={{ slug: featured.slug }}
                className="inline-flex items-center gap-2 rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold"
              >
                Open book page
              </Link>
              {featured.externalPurchaseUrl && (
                <a
                  href={featured.externalPurchaseUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-gold px-6 py-3 text-sm text-gold-luminous hover:bg-gold hover:text-obsidian"
                >
                  {featured.externalPurchaseLabel ?? "Read on Apple Books"} ↗
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-16 grid gap-10 md:grid-cols-3">
        {rest.map((b) => (
          <Link key={b.slug} to="/books/$slug" params={{ slug: b.slug }} className="group">
            {b.coverUrl ? (
              <div className="overflow-hidden rounded-lg border hairline bg-temple">
                <ResponsiveImage
                  src={b.coverUrl}
                  alt={b.coverAlt ?? `${b.title} — cover art`}
                  sizes="(max-width: 768px) 92vw, 33vw"
                  className="aspect-[3/4] h-auto w-full object-cover transition duration-500 group-hover:scale-[1.02]"
                />
              </div>
            ) : (
              <PlaceholderArt label={`[${b.title} cover]`} aspect="portrait" />
            )}
            <h2 className="mt-4 font-serif text-2xl group-hover:text-gold-luminous">{b.title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{b.subtitle}</p>
            <p className="mt-3 text-xs text-gold">Read more →</p>
          </Link>
        ))}
      </div>
    </section>
  );
}
